import { useState } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { Plus, Pencil, Trash2, RefreshCw, HelpCircle } from "lucide-react";
import { CalendarIcon } from "lucide-react";
import { useRecurringExpenses, RecurringExpenseFormData, FREQUENCY_OPTIONS } from "@/hooks/useRecurringExpenses";
import { useVehicles } from "@/hooks/useVehicles";
import { useExpenses } from "@/hooks/useExpenses";
import { format, parseISO } from "date-fns";
import { getScheduledDates } from "@/lib/dateUtils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/* ── component ──────────────────────────────────────────── */

export const RecurringExpensesTab = () => {
  const { recurringExpenses, isLoading, addRecurringExpense, updateRecurringExpense, deleteRecurringExpense, backfillAndCreate } = useRecurringExpenses();
  const { vehicles } = useVehicles();
  const { expenseTypes, paidByOptions } = useExpenses();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<RecurringExpenseFormData>({
    amount: 0,
    expense_type: "Other",
    frequency: "monthly",
    start_date: new Date().toISOString().split("T")[0],
  });

  // Confirmation dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewBackfill, setPreviewBackfill] = useState<string[]>([]);
  const [previewUpcoming, setPreviewUpcoming] = useState<string[]>([]);
  const [pendingFormData, setPendingFormData] = useState<RecurringExpenseFormData | null>(null);

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

  const resetForm = () => {
    setForm({
      amount: 0,
      expense_type: "Other",
      frequency: "monthly",
      start_date: new Date().toISOString().split("T")[0],
    });
    setEditingId(null);
  };

  const openAdd = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (re: typeof recurringExpenses[0]) => {
    setForm({
      vehicle_id: re.vehicle_id,
      amount: re.amount,
      description: re.description || "",
      frequency: re.frequency,
      start_date: re.start_date,
      next_generation_date: re.next_generation_date,
      is_active: re.is_active,
      expense_type: re.expense_type,
      paid_by: re.paid_by,
      paid_to: re.paid_to,
    });
    setEditingId(re.id);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (editingId) {
      // Editing — save directly, no preview
      updateRecurringExpense.mutate({ id: editingId, ...form });
      setDialogOpen(false);
      resetForm();
    } else {
      // Creating — show confirmation preview
      const { backfill, upcoming, nextGenerationDate } = getScheduledDates(form.start_date, form.frequency);
      const formWithNext: RecurringExpenseFormData = {
        ...form,
        next_generation_date: nextGenerationDate,
      };
      setPreviewBackfill(backfill);
      setPreviewUpcoming(upcoming);
      setPendingFormData(formWithNext);
      setDialogOpen(false);
      setConfirmOpen(true);
    }
  };

  const handleConfirm = () => {
    if (!pendingFormData) return;
    backfillAndCreate.mutate({
      formData: pendingFormData,
      backfillDates: previewBackfill,
    });
    setConfirmOpen(false);
    setPendingFormData(null);
    resetForm();
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
    setDialogOpen(true); // re-open the form so they can adjust
  };

  const handleDelete = (id: string) => {
    if (confirm("Delete this recurring expense template?")) {
      deleteRecurringExpense.mutate(id);
    }
  };

  const getVehicleName = (vehicleId: string | null) => {
    if (!vehicleId) return "Operations";
    const v = vehicles?.find((v) => v.id === vehicleId);
    return v ? v.name || `${v.year} ${v.make} ${v.model}` : "Unknown";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <Alert className="border-primary/20 bg-primary/5">
          <RefreshCw className="h-4 w-4 text-primary" />
          <AlertDescription className="text-sm text-muted-foreground">
            Recurring templates are processed automatically each day. When a template's next due date arrives, a real expense is created in the <strong>Expenses</strong> tab. Edit amounts here to adjust future payments.
            <br /><br />
            If you have a loan, you can edit future payment amounts here. For example, if you want to make extra payments on your loan, simply increase the Principal payment here.
          </AlertDescription>
        </Alert>

        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">Recurring Expenses</h2>
          <Button onClick={openAdd} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Recurring Expense
          </Button>
        </div>

        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-semibold text-xs">Description</TableHead>
                <TableHead className="font-semibold text-xs">Vehicle</TableHead>
                <TableHead className="font-semibold text-xs">Amount</TableHead>
                <TableHead className="font-semibold text-xs">Frequency</TableHead>
                <TableHead className="font-semibold text-xs">Expense Type</TableHead>
                <TableHead className="font-semibold text-xs">Paid By</TableHead>
                <TableHead className="font-semibold text-xs">Next Date</TableHead>
                <TableHead className="font-semibold text-xs">Status</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {recurringExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                    No recurring expenses yet. Add a template to automate regular costs.
                  </TableCell>
                </TableRow>
              ) : (
                recurringExpenses.map((re) => (
                  <TableRow key={re.id} className="group">
                    <TableCell className="font-medium">{re.description || "—"}</TableCell>
                    <TableCell>{getVehicleName(re.vehicle_id)}</TableCell>
                    <TableCell>{fmt(re.amount)}</TableCell>
                    <TableCell className="capitalize">
                      {FREQUENCY_OPTIONS.find((f) => f.value === re.frequency)?.label || re.frequency}
                    </TableCell>
                    <TableCell>{re.expense_type || "—"}</TableCell>
                    <TableCell>{re.paid_by || "—"}</TableCell>
                    <TableCell>{format(new Date(re.next_generation_date + "T00:00:00"), "MMM d, yyyy")}</TableCell>
                    <TableCell>
                      <Badge variant={re.is_active ? "default" : "secondary"}>
                        {re.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="p-1">
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(re)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(re.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add / Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit" : "Add"} Recurring Expense</DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update this recurring expense template."
                  : "Create a template that will automatically generate expenses on a schedule."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={form.description || ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. Monthly Insurance"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount ($)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.amount || ""}
                    onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expense Type</Label>
                  <Select
                    value={form.expense_type || ""}
                    onValueChange={(v) => setForm({ ...form, expense_type: v || null })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {[...expenseTypes, "Interest", "Principal Paydown"].filter((v, i, a) => a.indexOf(v) === i).map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FREQUENCY_OPTIONS.map((f) => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Vehicle</Label>
                  <Select
                    value={form.vehicle_id || "operations"}
                    onValueChange={(v) => setForm({ ...form, vehicle_id: v === "operations" ? null : v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operations">Operations</SelectItem>
                      {vehicles?.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name || `${v.year} ${v.make} ${v.model}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Start Date
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[240px]">
                        The date this recurring expense first began. If set in the past, missed payments will be backfilled as expense records.
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal h-9 px-3",
                          !form.start_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.start_date
                          ? format(parseISO(form.start_date), "MMM d, yyyy")
                          : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-popover" align="start">
                      <Calendar
                        mode="single"
                        selected={form.start_date ? parseISO(form.start_date) : undefined}
                        defaultMonth={form.start_date ? parseISO(form.start_date) : undefined}
                        onSelect={(date) => {
                          if (date) setForm({ ...form, start_date: format(date, "yyyy-MM-dd") });
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {editingId && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      Override Next Due Date
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[240px]">
                          The next date an expense record will be auto-generated. Change this to skip a month or correct the schedule.
                        </TooltipContent>
                      </Tooltip>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal h-9 px-3",
                            !form.next_generation_date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {form.next_generation_date
                            ? format(parseISO(form.next_generation_date), "MMM d, yyyy")
                            : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-popover" align="start">
                        <Calendar
                          mode="single"
                          selected={form.next_generation_date ? parseISO(form.next_generation_date) : undefined}
                          defaultMonth={form.next_generation_date ? parseISO(form.next_generation_date) : undefined}
                          onSelect={(date) => {
                            if (date) setForm({ ...form, next_generation_date: format(date, "yyyy-MM-dd") });
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Paid By</Label>
                  <Select
                    value={form.paid_by || ""}
                    onValueChange={(v) => setForm({ ...form, paid_by: v || null })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {paidByOptions.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Paid To</Label>
                  <Input
                    value={form.paid_to || ""}
                    onChange={(e) => setForm({ ...form, paid_to: e.target.value || null })}
                    placeholder="e.g. Capital One"
                  />
                </div>
              </div>

              {editingId && (
                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.is_active ?? true}
                    onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                  />
                  <Label>Active</Label>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={!form.amount || !form.expense_type}>
                {editingId ? "Save Changes" : "Create Template"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmation Preview Dialog */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Confirm Recurring Expense</DialogTitle>
              <DialogDescription>
                Review the payments that will be generated for this recurring expense.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Backfill section */}
              {previewBackfill.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    Expenses to create now
                  </h3>
                  <div className="border rounded-md">
                    <div className="max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="text-xs font-semibold">Date</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {previewBackfill.map((date) => (
                            <TableRow key={date}>
                              <TableCell className="py-1.5 text-sm">
                                {format(new Date(date + "T00:00:00"), "MMM d, yyyy")}
                              </TableCell>
                              <TableCell className="py-1.5 text-sm text-right">
                                {fmt(pendingFormData?.amount ?? 0)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {previewBackfill.length} expense{previewBackfill.length === 1 ? "" : "s"} · {fmt(previewBackfill.length * (pendingFormData?.amount ?? 0))} total
                  </p>
                </div>
              )}

              {/* Upcoming section */}
              {previewUpcoming.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    Upcoming scheduled payments (auto-generated)
                  </h3>
                  <div className="border rounded-md border-dashed">
                    <Table>
                      <TableBody>
                        {previewUpcoming.map((date) => (
                          <TableRow key={date} className="text-muted-foreground">
                            <TableCell className="py-1.5 text-sm">
                              {format(new Date(date + "T00:00:00"), "MMM d, yyyy")}
                            </TableCell>
                            <TableCell className="py-1.5 text-sm text-right">
                              {fmt(pendingFormData?.amount ?? 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-xs text-muted-foreground italic">
                    These will be created automatically on their due dates.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCancelConfirm}>Cancel</Button>
              <Button
                onClick={handleConfirm}
                disabled={backfillAndCreate.isPending}
              >
                {backfillAndCreate.isPending ? "Creating…" : "Confirm & Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};
